/*#
# Notes import: TODO.md and UPSTREAM.md as tasks

Every repo in the ecosystem keeps its open work in two markdown files:
`TODO.md` (what to do, `- [ ]` and `- [x]` items under headings) and
`UPSTREAM.md` (what it is waiting on in other repos, each item naming the
issue it filed). This module turns those lists into tasks, so the board can
hold the work that lives in files as well as the work that lives in GitHub.

Each top-level list item becomes one task, and it carries its why, not
just its what (board #163):

- **The title** is the item's bold lead (`**Replica vs. a restarted host**
  (M1): …`), else its first paragraph when that is short, else its first
  clause — cut at a `;`, ` — `, `: ` or a sentence end (never at "vs."),
  or before a parenthetical — so a title is never cut mid-sentence unless
  nothing else fits.
- **The body** is the item's own words — the whole lead again when the
  title is only part of it, indented continuation lines, nested bullets,
  and unindented "lazy" continuation lines, as markdown reads them.
- **Context:** prose under the item's heading (outside any item) comes
  along as a quoted block — often the reason the list exists.
- **Findings:** given the review files it cites (`references`), an item that
  names a finding — `M1`, `F2`, `B3` — gets that finding quoted from the
  review, with a link. The file is the one the item or its heading cites
  (a glob like `reviews/0.5.0-review-\*.md` works), or "the fifth
  re-review" → `…re-review-5.md`.
- **The source** closes the body: the file and line, linked at the imported
  commit when `repoUrl` is given, and the heading path.

The CLI (`virta project notes`, `bin/notes.ts`, `virta project onboard`) fills in the path,
the repo's blob URL at HEAD, and the review files the notes cite. An open item arrives in **backlog** (a
roadmap item in **freezer**): it is acknowledged work, not something new
in the inbox. A done item — `- [x]`, a leading ✅, a struck-through
`~~lead~~`, **CLOSED** / **DONE**, or "— done" — arrives closed. Every GitHub issue URL in the item
becomes a link tag: `upstream:tosijs-ui#173` when the issue is in another
repo, `github:12` when it is this project's own. The tags `source:notes`
and `notes:todo` / `notes:upstream` say where it came from.

Ids are deterministic — a hash of the item's text — so importing the same
file again adds nothing (the hash stops at an item's first lazy line, as the
first importer's did, so the richer parse kept every id), and a file that gained three items adds three
tasks. An item whose wording changed is a new task; the old one stays,
because the log never forgets and the file is not the truth once it has
been imported (the same rule as GitHub issues after onboarding). Close the
stale one on the board.

```
virta notes --project tosijs-virta --host https://… TODO.md UPSTREAM.md
```

The `notes` verb does the same for an agent, given the file's text.
*/

/*{ "parent": "Reference", "order": 91 }*/

import type { EventDraft } from './model'
import { hashText } from './palette'
import type { VirtaStore } from './store'
import { normalizeTag } from './tags'

export interface NoteItem {
  /** stable across imports: a hash of the item's text */
  id: string
  title: string
  body: string
  tags: string[]
  closed: boolean
  /** the heading path the item sat under */
  section: string[]
}

export interface NotesOptions {
  project: string
  /** the file's name; decides the `notes:` tag (`todo`, `upstream`, `roadmap`, else `notes`) */
  file?: string
  /** the file's repo-relative path, for the source line at the foot of each body (default `file`) */
  path?: string
  /**
   * The repo's blob URL at the imported commit, ending in `/`
   * (`https://github.com/o/r/blob/<sha>/`): the source line and each quoted
   * finding link to their line.
   */
  repoUrl?: string
  /**
   * Files the notes cite — review reports — by repo-relative path. An item
   * that names a finding (`M1`, `F2`, `B3`) and cites a file (directly, by a
   * glob in its heading, or as "the fifth re-review") gets that finding
   * quoted in its body: the why, not just the what.
   */
  references?: Record<string, string>
}

const ITEM = /^([ \t]*)[-*]\s+(?:\[([ xX])\]\s+)?(.*)$/
const ISSUE = /github\.com\/([\w.-]+)\/([\w.-]+)\/(?:issues|pull)\/(\d+)/g
const HEADING = /^(#{1,6})\s+(.*)$/
const LABEL_LINE = /^([A-Z][^:]{1,100}):\s*$/

function kindOf(file: string | undefined): string {
  const base = (file ?? '')
    .split('/')
    .pop()!
    .replace(/\.md$/i, '')
    .toLowerCase()
  return ['todo', 'upstream', 'roadmap', 'downstream'].includes(base)
    ? base
    : 'notes'
}

/**
 * Is this item done? Every way the repos mark it: a checked box, a leading
 * ✅ / ✔ / ✓ / ☑ (a ✅ later in the lead too, unless it says FIXED UPSTREAM), a struck-through lead (`~~…~~`), **CLOSED** / **DONE** /
 * `DONE:` at the start, or "— done" (any dash) in the lead. Not "fixed" or
 * "landed" on their own: "fixed capacity" is a design note, not a status.
 */
export function isDone(checked: string | undefined, lead: string): boolean {
  const t = lead.trim()
  return (
    checked?.toLowerCase() === 'x' ||
    /^[✅✔✓☑]/.test(t) ||
    // a ✅ after the title says the same (board #1284: "Phase 0 — baseline.
    // ✅ Re-run 2026-09-05"), except FIXED UPSTREAM, which stays open
    /\s✅(?!\uFE0F?\s*(\*\*)?\s*fixed upstream)/i.test(t) ||
    /^~~.+~~/.test(t) ||
    /^(\*\*(closed|done|resolved|fixed)\b|(closed|done|resolved|fixed)\s*[:—–-])/i.test(
      t
    ) ||
    /\s[—–-]\s*done\b/i.test(t)
  )
}

/**
 * A lead without its "this is finished" marker (#1086): the status is the
 * task's closed state, so the title needn't say it. The ✅, a
 * \`**RESOLVED** (pkg@1.2) —\`, and the strike-through come off; the body
 * keeps the item's full text.
 */
export function withoutDoneMarker(lead: string): string {
  const out = lead
    .trim()
    .replace(/^[✅✔✓☑]\uFE0F?\s*/, '')
    .replace(
      /^\*\*(resolved|fixed|closed|done)\b[^*]*\*\*\s*(\([^)]*\))?\s*[:—–-]?\s*/i,
      ''
    )
    .replace(/^~~(.+?)~~/, '$1')
    .trim()
  return out || lead.trim()
}

/** Markdown emphasis off the first line; the text itself is kept. */
function plainTitle(line: string): string {
  return line
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

const ABBREVIATION = /\b(?:vs|e\.g|i\.e|etc|cf|approx)\.$/i
const TITLE_MAX = 140

/**
 * An item's title: its bold lead (`**Replica vs. a restarted host** (M1…)`),
 * else the whole lead when it is short, else its first clause — cut at a
 * `;`, ` — `, `: ` or a sentence end, never mid-word unless nothing else
 * fits. `whole` says the title is the entire lead (else the body repeats it).
 */
export function titleOf(lead: string): { title: string; whole: boolean } {
  const plain = plainTitle(lead)
  // a boundary inside `code` or [link text](url) is not a clause end (#760):
  // search a copy with those spans blanked out, cut the original
  const masked = plain
    .replace(/`[^`]*`/g, (m) => 'x'.repeat(m.length))
    .replace(/\[[^\]]*\]\([^)]*\)/g, (m) => 'x'.repeat(m.length))
    // nor inside a parenthetical: a title never ends inside one (#2590)
    .replace(/\([^()]*\)/g, (m) => `(${'x'.repeat(m.length - 2)})`)
  // a `**` inside a code span doesn't close the bold (board #2505)
  const trimmed = lead.trim()
  const bold = /^\*\*(.+?)\*\*/.exec(
    trimmed.replace(/`[^`]*`/g, (m) => 'x'.repeat(m.length))
  )
  // a bold lead that is only a reference (`**[tosijs#40](…)** — re-vendor
  // at…`) labels the item; the words after it are the title (2026-10-01,
  // tosijs-floorplan)
  const ref = bold
    ? /^(?:\[([^\]]+)\]\([^)]*\)|([\w.-]+#\d+))$/.exec(bold[1]!.trim())
    : null
  if (bold && ref) {
    const rest = trimmed
      .slice(bold[0].length)
      .replace(/^[\s—–:,-]+/, '')
      .trim()
    if (rest) {
      const label = ref[1] ?? ref[2]!
      const inner = titleOf(rest)
      return { title: `${label}: ${inner.title}`, whole: false }
    }
  }
  if (bold) {
    const title = plainTitle(trimmed.slice(2, 2 + bold[1]!.length)).replace(
      /[\s.:;,—–-]+$/,
      ''
    )
    if (title.length >= 8 && title.length <= TITLE_MAX)
      return { title, whole: title === plain }
  }
  if (plain.length <= 120) return { title: plain, whole: true }
  const boundary = /(; | — | – |: |\. (?=[A-Z`(*"]))/g
  for (const m of masked.matchAll(boundary)) {
    if (m.index! < 30) continue
    if (m.index! > TITLE_MAX) break
    const head = plain.slice(0, m.index! + (m[0].startsWith('.') ? 1 : 0))
    if (m[0].startsWith('.') && ABBREVIATION.test(head)) continue
    return { title: head.replace(/[\s;:—–]+$/, ''), whole: false }
  }
  // no clause fits: a lead that fits is the title, else end before a
  // parenthetical (the detail), else at a word
  if (plain.length <= TITLE_MAX) return { title: plain, whole: true }
  const paren = masked.lastIndexOf(' (', TITLE_MAX)
  if (paren >= 40) return { title: plain.slice(0, paren), whole: false }
  const cut = masked.lastIndexOf(' ', TITLE_MAX)
  return {
    title: plain.slice(0, cut > 60 ? cut : TITLE_MAX).trim() + '…',
    whole: false,
  }
}

const FINDING_ID = /\b([BMF]\d{1,2})\b/g
const ORDINALS = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
]
const MD_PATH = /[\w.-]+\/[\w.*\\-]+\.md\b/g

/** The reference files a text cites: a path, or a glob like `reviews/0.5.0-review-\*.md`. */
function citedFiles(text: string, keys: string[]): string[] {
  const found: string[] = []
  for (const [raw] of text.matchAll(MD_PATH)) {
    const glob = raw.replace(/\\\*/g, '*')
    const re = new RegExp(
      `^${glob
        .split('*')
        .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
        .join('[^/]*')}$`
    )
    for (const key of keys)
      if (re.test(key) && !found.includes(key)) found.push(key)
  }
  return found
}

/** "the fifth re-review" → the file for it: `…re-review-5.md` (the first is `…re-review.md`), preferring a version the item's other citations share. */
function ordinalFile(
  text: string,
  keys: string[],
  near: string[]
): string | null {
  const m =
    /\b(?:the\s+)?(?:(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth)\s+)?re-review\b/i.exec(
      text
    )
  if (!m) return null
  const n = m[1] ? ORDINALS.indexOf(m[1].toLowerCase()) + 1 : 1
  const suffix = n === 1 ? 're-review.md' : `re-review-${n}.md`
  const matches = keys.filter((k) => k.endsWith(suffix))
  const version = (k: string) => /(\d+\.\d+\.\d+)/.exec(k)?.[1]
  const wanted = near.map(version).filter(Boolean)
  return matches.find((k) => wanted.includes(version(k))) ?? matches[0] ?? null
}

const QUOTE_MAX = 700

/** A finding's own text in a review file: its heading section, or its bullet and continuation lines. */
export function findingIn(
  text: string,
  id: string
): { line: number; excerpt: string } | null {
  const lines = text.split(/\r?\n/)
  const start = new RegExp(
    `^(?:(#{1,6})\\s+|(\\s*)[-*]\\s+(?:\\[[ xX]\\]\\s+)?)(?:\\*\\*)?${id}(?![\\w-])`
  )
  for (let i = 0; i < lines.length; i++) {
    const m = start.exec(lines[i]!)
    if (!m) continue
    const block = [lines[i]!]
    const indent = (m[2] ?? '').length
    for (let j = i + 1; j < lines.length; j++) {
      const l = lines[j]!
      if (m[1]) {
        if (/^#{1,6}\s/.test(l)) break
      } else {
        const bullet = /^(\s*)[-*]\s/.exec(l)
        if (bullet && bullet[1]!.length <= indent) break
        if (l.trim() && !/^\s/.test(l)) break
      }
      block.push(l)
    }
    let excerpt = block
      .map((l) => l.trim())
      .filter(Boolean)
      .join(' ')
      .replace(/^#{1,6}\s+/, '')
      .replace(/^[-*]\s+(?:\[[ xX]\]\s+)?/, '')
    if (excerpt.length > QUOTE_MAX) {
      const cut = excerpt.lastIndexOf(' ', QUOTE_MAX)
      excerpt = excerpt.slice(0, cut > 200 ? cut : QUOTE_MAX) + ' …'
    }
    return { line: i + 1, excerpt }
  }
  return null
}

/**
 * The column that makes a table a list of items, and gives each row its
 * title: Finding (the practices' format), What (tjs-lang's), Task, Item
 * (#760). A table with none of them is data.
 */
const LIST_COLUMNS = ['finding', 'what', 'task', 'item']
const FINDING_HEADER = new RegExp(
  `^\\|(?:.*\\|)?\\s*\\**(${LIST_COLUMNS.join('|')})\\**\\s*\\|`,
  'i'
)
/**
 * An UPSTREAM section that is itself a finding: \`## <owner> — <finding>\`
 * (tjs-lang's format, #760). The whole section is one task.
 */
const FINDING_SECTION = /^(.+?)\s+[—–]\s+\S/

/**
 * A repo-like name: what the owner half of `owner — finding` must look like
 * (board #2505). Lowercase (`tosijs-ui`, `bun`, `@scope/pkg`) or with its
 * org (`microsoft/TypeScript`): a capitalised word (`Strategic`) is prose.
 */
const REPO_NAME = /^(?:[a-z0-9@][\w@./-]*|[\w.-]+\/[\w.-]+)$/

/** The owner a heading names, when it is `<repo> — <finding>`; '' otherwise. */
function headingOwner(heading: string): string {
  const m = FINDING_SECTION.exec(heading)
  const owner = m ? m[1]!.replace(/[`*_~]/g, '').trim() : ''
  return REPO_NAME.test(owner) ? owner : ''
}

/** A line that cites an issue: a GitHub issue or PR URL, or an `**Issue:**` line. */
const CITES_ISSUE =
  /github\.com\/[\w.-]+\/[\w.-]+\/(?:issues|pull)\/\d+|^\s*\**(?:issue|upstream)\**\s*:/i

/**
 * Which `##` headings of an UPSTREAM file start a finding section (board
 * #2505): one that names its repo (`## tosijs-ui — <finding>`), or one whose
 * prose cites an issue (`## <title>` then `**Issue:** <url>`) and has no
 * list of its own. An em dash used as punctuation (`## Strategic — …`) is
 * neither.
 */
function findingHeadings(lines: string[]): Set<number> {
  const found = new Set<number>()
  for (let n = 0; n < lines.length; n++) {
    const h = /^##\s+(.*)$/.exec(lines[n]!)
    if (!h) continue
    let end = n + 1
    while (end < lines.length && !/^#{1,2}\s/.test(lines[end]!)) end++
    const under = lines.slice(n + 1, end)
    // a section of bullets or table rows is a list: those are its items
    const listed = under.some((l) => /^[-*]\s|^\|/.test(l))
    if (
      headingOwner(h[1]!) ||
      (!listed && under.some((l) => CITES_ISSUE.test(l)))
    )
      found.add(n)
  }
  return found
}

/** \`section:<slug>\` for the heading an item sits under: short, filterable. */
function sectionSlug(heading: string): string {
  const words = plainTitle(heading)
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[`*_~]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  if (words.length <= 40) return words
  const cut = words.lastIndexOf('-', 40)
  return words.slice(0, cut > 10 ? cut : 40)
}

/** A short form of a heading, to prefix an enumerated item's title: before any " — " or parenthetical. */
function headingLabel(heading: string): string {
  return plainTitle(heading)
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .split(/\s+[—–]\s+/)[0]!
    .trim()
    .replace(/[:.]+$/, '')
}
/** An item that is one entry of a list its heading names: \`(b) …\`, \`a) …\`, \`3. …\`. */
const ENUMERATED = /^\**\(?[a-z0-9]{1,3}[).]\s/i
/** A table's rule row: `| --- | :---: |`. */
const TABLE_RULE = /^\|(\s*:?-{3,}:?\s*\|)+\s*$/
/** A table row's cells; an escaped `\|` stays in its cell, as a `|`. */
const cellsOf = (row: string): string[] =>
  row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/)
    .map((c) => c.trim().replace(/\\\|/g, '|'))

/** Tags the importer writes from what a notes file says, not what an item says. */
export const MOSTLY_DONE_TAG = 'mostly-done'
/** \`section:<slug>\`: the heading an imported item sat under (#760). */
export const SECTION_KEY = 'section'
export const FIXED_UPSTREAM_TAG = 'fixed-upstream'

type HeadingState = 'closed' | 'mostly' | null
const DONE_WORDS = 'done|shipped|resolved|fixed|closed'
/**
 * What a heading says about the items under it (board #178): `## Wreck fall
 * — DONE 2026-08-26`, `## Resolved`, `## Infrastructure (Done)` close them;
 * `⟵ mostly SHIPPED` marks them; "not done", UNRESOLVED and prose that merely
 * mentions a word ("how issues get closed") say nothing. The word counts in
 * capitals anywhere, or in any case as the whole heading or a parenthetical.
 */
export function headingState(text: string): HeadingState {
  const plain = plainTitle(text).replace(/[`*_~]/g, '')
  if (new RegExp(`\\bnot\\s+(yet\\s+)?(${DONE_WORDS})\\b`, 'i').test(plain))
    return null
  const said =
    new RegExp(`\\b(${DONE_WORDS.toUpperCase()})\\b`).test(plain) ||
    new RegExp(`^\\W*(${DONE_WORDS})\\W*$`, 'i').test(plain) ||
    new RegExp(`\\((${DONE_WORDS})\\)`, 'i').test(plain)
  if (!said) return null
  return /\b(mostly|partly|partially|largely)\b/i.test(plain)
    ? 'mostly'
    : 'closed'
}

/** A markdown blockquote of some text. */
const quoted = (text: string) =>
  text
    .split('\n')
    .map((l) => `> ${l}`.trimEnd())
    .join('\n')

/**
 * The items of a notes file — top-level list items, and the rows of any
 * table with a Finding column (board #179) — plus what was deliberately not
 * imported: in an UPSTREAM file that keeps its findings in tables, its
 * bullets (prose, not items), so nothing is dropped silently.
 */
/** A top-level list item the import did not take as a task, and why. */
export interface SkippedLine {
  line: number
  text: string
  why: 'prose in a tables file' | 'a pointer file' | 'a section with no items'
}

/** A link to a virta board (`…/host/#?virta.scope=…`): a file that has one is about the board. */
const BOARD_LINK = /[#?&]virta\.scope=|virta\.tosijs\.net/

export function parseNotesReport(
  text: string,
  options: NotesOptions
): { items: NoteItem[]; skipped: SkippedLine[] } {
  const kind = kindOf(options.file)
  const project = normalizeTag(options.project)
  const items: NoteItem[] = []
  const headings: {
    level: number
    text: string
    intro: string[]
    /** what the heading says about its items: DONE / SHIPPED …, or mostly */
    state: HeadingState
    /** where it is, whether prose sits under it, and how many items it gave */
    line: number
    prose: boolean
    items: number
  }[] = []
  const skipped: SkippedLine[] = []
  // an UPSTREAM file that keeps its findings in tables, or in \`## owner —
  // finding\` sections: those are the list, and a bullet elsewhere is prose
  // (a note's example list), not an item
  const lines0 = text.split(/\r?\n/)
  const sectionStarts =
    kind === 'upstream' ? findingHeadings(lines0) : new Set<number>()
  const findingSections = sectionStarts.size > 0
  const tablesOnly =
    kind === 'upstream' &&
    (findingSections || lines0.some((l) => FINDING_HEADER.test(l)))
  // a file that links to its board and has no checkbox is a pointer (what
  // onboard advises TODO.md becomes): its bullets are history, never tasks
  // (board #2413). Checkboxes don't make the others notes: real lists mix
  // them with plain task bullets (tjs-lang's), so a checkbox keeps a
  // pointer's own list importable and that is all it decides
  const usesCheckboxes = lines0.some((l) => {
    const m = ITEM.exec(l)
    return !!m && m[1]!.length === 0 && m[2] !== undefined
  })
  const pointer = !usesCheckboxes && BOARD_LINK.test(text)
  /** An UPSTREAM finding section being collected: its heading and every line under it. */
  let section: { heading: string; line: number; lines: string[] } | null = null
  let label = ''
  let current: {
    lines: string[]
    checked: string | undefined
    line: number
    /** where the first lazy continuation line joined: the id's text stops there */
    idEnd?: number
  } | null = null
  const references = options.references ?? {}
  const referenceKeys = Object.keys(references)
  const sourcePath = options.path ?? options.file ?? ''
  const link = (file: string, line: number) =>
    options.repoUrl
      ? `[${file}:${line}](${options.repoUrl}${file}#L${line})`
      : `${file}:${line}`

  const flush = () => {
    if (!current) return
    // the lead paragraph is the title: the first line and its wrapped
    // continuation, up to a blank line or a nested bullet
    const lines = current.lines
    let end = 0
    while (
      end + 1 < lines.length &&
      lines[end + 1]!.trim() !== '' &&
      !/^[ \t]+[-*]\s/.test(lines[end + 1]!)
    )
      end++
    const first = lines
      .slice(0, end + 1)
      .map((l) => l.trim())
      .join(' ')
    const rest = lines.slice(end + 1)
    const full = [first, ...rest].join('\n')
    // the id hashes the text the first importer saw, which ended an item at
    // its first unindented continuation line: ids stay stable across the fix
    const idText =
      current.idEnd === undefined
        ? full
        : (() => {
            const kept = lines.slice(0, current.idEnd)
            let e = 0
            while (
              e + 1 < kept.length &&
              kept[e + 1]!.trim() !== '' &&
              !/^[ \t]+[-*]\s/.test(kept[e + 1]!)
            )
              e++
            return [
              kept
                .slice(0, e + 1)
                .map((l) => l.trim())
                .join(' '),
              ...kept.slice(e + 1),
            ].join('\n')
          })()
    const bodyLines = rest.map((l) => l.replace(/^\s{0,6}/, ''))
    emit({
      first,
      bodyLines,
      full,
      idText,
      line: current.line,
      closed: isDone(current.checked, first),
      tags: [],
    })
    current = null
  }

  /** One item, from a list item or a table row: title, body, tags, id. */
  const emit = (entry: {
    first: string
    bodyLines: string[]
    full: string
    idText: string
    line: number
    closed: boolean
    tags: string[]
    /** a finding section: its heading is the title, so no section tag */
    isSection?: boolean
  }) => {
    const { first, bodyLines, full, idText } = entry
    for (const h of headings) h.items++
    const section = [...headings.map((h) => h.text), ...(label ? [label] : [])]
    const cleaned = entry.closed ? withoutDoneMarker(first) : first
    const { title: plainTitleText, whole: wholeLead } = titleOf(cleaned)
    // the body repeats the lead whenever the title isn't all of it
    const whole = wholeLead && cleaned === first
    // the heading an item sits under gives it meaning (#760): a tag to find
    // and group by, and — for an entry of an enumerated list — a title prefix
    const nearest = [...headings].reverse().find((h) => h.level >= 2)
    const sectionTag =
      nearest && !entry.isSection ? sectionSlug(nearest.text) : ''
    const title =
      nearest && !entry.isSection && ENUMERATED.test(first.trim())
        ? `${headingLabel(nearest.text)}: ${plainTitleText}`
        : plainTitleText
    const parts: string[] = []
    // the item's own words: the lead again when the title is only part of it
    const own = [whole ? '' : first, ...bodyLines].join('\n').trim()
    if (own) parts.push(own)
    // the prose under the nearest heading that has some: why this list exists
    const withIntro = [...headings].reverse().find((h) => h.intro.length)
    if (withIntro) {
      let intro = withIntro.intro.join('\n').trim()
      if (intro.length > QUOTE_MAX)
        intro = intro.slice(0, intro.lastIndexOf(' ', QUOTE_MAX)) + ' …'
      parts.push(`Context (${withIntro.text}):\n${quoted(intro)}`)
    }
    // the findings it cites, quoted from the review that made them
    if (referenceKeys.length) {
      const near = citedFiles(
        [full, ...headings.map((h) => h.text)].join('\n'),
        referenceKeys
      )
      const ordinal = ordinalFile(full, referenceKeys, near)
      const candidates = ordinal ? [ordinal] : near
      const ids = [...new Set([...full.matchAll(FINDING_ID)].map((m) => m[1]!))]
      let quotes = 0
      for (const id of ids) {
        let perId = 0
        for (const file of candidates) {
          if (quotes >= 3 || perId >= 2) break
          const hit = findingIn(references[file]!, id)
          if (!hit) continue
          parts.push(
            `Finding ${id} (${link(file, hit.line)}):\n${quoted(hit.excerpt)}`
          )
          quotes++
          perId++
        }
      }
    }
    // where it came from, last: the file and line, and the heading path
    const where = sourcePath ? `Source: ${link(sourcePath, entry.line)}` : ''
    const path = section.length ? section.join(' › ') : ''
    const footer = [where, path].filter(Boolean).join(' · ')
    if (footer) parts.push(footer)
    const body = parts.join('\n\n').trim()
    // the nearest heading that says something about its items: a heading
    // marked DONE / SHIPPED / RESOLVED closes them, "mostly SHIPPED" tags
    // them (board #178)
    const said = entry.isSection
      ? null
      : [...headings].reverse().find((h) => h.state)?.state
    const closed = entry.closed || said === 'closed'
    // an item in a notes file is acknowledged work, not untriaged: backlog
    // (a roadmap item is parked: freezer); a done one is closed and needs no lane
    const tags = new Set<string>([
      `project:${project}`,
      'source:notes',
      `notes:${kind}`,
      ...(closed
        ? []
        : [`status:${kind === 'roadmap' ? 'freezer' : 'backlog'}`]),
      ...(said === 'mostly' && !closed ? [MOSTLY_DONE_TAG] : []),
      ...(sectionTag ? [`${SECTION_KEY}:${sectionTag}`] : []),
      ...entry.tags,
    ])
    for (const m of full.matchAll(ISSUE)) {
      const [, , repo, num] = m
      tags.add(repo === project ? `github:${num}` : `upstream:${repo}#${num}`)
    }
    const hash = hashText(idText.replace(/\s+/g, ' ').trim())
      .toString(16)
      .padStart(8, '0')
    items.push({
      id: `notes:${project}:${kind}:${hash}`,
      title,
      body,
      tags: Array.from(tags),
      closed,
      section,
    })
  }

  /** A table: a list of items when it has a Finding column; data otherwise. */
  const table = (block: { line: number; text: string }[]) => {
    const header = cellsOf(block[0]!.text).map((c) =>
      plainTitle(c).toLowerCase()
    )
    const at = (name: string) => header.findIndex((h) => h === name)
    const finding = LIST_COLUMNS.map(at).find((i) => i >= 0) ?? -1
    if (finding < 0 || block.length < 3 || !TABLE_RULE.test(block[1]!.text))
      return
    const owner = at('owner')
    const status = at('status')
    for (const row of block.slice(2)) {
      const cells = cellsOf(row.text)
      const lead = (cells[finding] ?? '').trim()
      if (!lead) continue
      const ownerCell = owner < 0 ? '' : (cells[owner] ?? '').trim()
      const statusCell = status < 0 ? '' : (cells[status] ?? '').trim()
      const tags: string[] = []
      // a struck-out owner: fixed upstream, the workaround not yet walked —
      // still work, so it stays open, marked
      if (/^~~.*~~$/.test(ownerCell)) tags.push(FIXED_UPSTREAM_TAG)
      const ownerName = ownerCell.replace(/[~`*]/g, '').trim()
      if (ownerName && !ISSUE.test(row.text) && /^[\w.-]+$/.test(ownerName))
        tags.push(`upstream:${ownerName}`)
      ISSUE.lastIndex = 0
      const others = header
        .map((h, i) =>
          i === finding || !cells[i]?.trim() ? '' : `${h}: ${cells[i]!.trim()}`
        )
        .filter(Boolean)
      emit({
        first: lead,
        bodyLines: others,
        full: row.text,
        idText: row.text,
        line: row.line,
        // a row is judged like a list item (#1086): its Finding cell led by
        // ✅, **RESOLVED** or struck through (~~…~~); or a Status cell that
        // says closed; or a **FIXED** / **CLOSED** anywhere in the row
        closed:
          isDone(undefined, lead) ||
          /^\W*(closed|fixed|done|resolved)\b/i.test(statusCell) ||
          /\*\*(FIXED|CLOSED|DONE|RESOLVED)\*\*/.test(row.text),
        tags,
      })
    }
  }

  /** A finding section, as one task: the heading, the prose under it (#760). */
  const flushSection = () => {
    if (!section) return
    const { heading, line, lines } = section
    section = null
    // a pointer file is history: its sections are not tasks either
    if (pointer) {
      skipped.push({ line, text: heading, why: 'a pointer file' })
      return
    }
    const owner = headingOwner(heading)
    const body = lines.join('\n').trim()
    const tags: string[] = []
    // "✅ FIXED UPSTREAM": fixed there, the workaround here still to walk —
    // still work, so it stays open, marked (as a struck owner does)
    const fixed = /fixed upstream/i.test(heading)
    if (fixed) tags.push(FIXED_UPSTREAM_TAG)
    // a short reference, \`tosijs-ui#190\`, names an issue as surely as a URL
    const short = [
      ...`${heading}\n${body}`.matchAll(
        /(?<![\w/.#-])([a-z][\w.-]*)#(\d+)\b/gi
      ),
    ].map((m) => `upstream:${m[1]!.toLowerCase()}#${m[2]}`)
    tags.push(...short)
    if (owner && !short.length && !ISSUE.test(`${heading}\n${body}`))
      tags.push(`upstream:${owner.toLowerCase()}`)
    ISSUE.lastIndex = 0
    const state = fixed ? null : headingState(heading)
    emit({
      first: heading.replace(/\s+[—–]\s+✅.*$/, ''),
      bodyLines: body ? [body] : [],
      full: `${heading}\n${body}`,
      idText: `${heading}\n${body}`,
      line,
      closed: state === 'closed',
      tags,
      isSection: true,
    })
  }

  /**
   * A section with prose under it that gave no item: said, not dropped
   * silently (board #2505) — it may be work written as paragraphs. The
   * title (`#`) is the file's own, and a pointer's prose is expected.
   */
  const declined = (h: (typeof headings)[number]) => {
    if (h.level >= 2 && h.prose && h.items === 0 && !pointer)
      skipped.push({
        line: h.line,
        text: h.text,
        why: 'a section with no items',
      })
  }

  const rows = text.split(/\r?\n/)
  for (let n = 0; n < rows.length; n++) {
    const raw = rows[n]!
    const heading = HEADING.exec(raw)
    // a finding section takes everything under it, sub-headings included
    if (section && !(heading && heading[1]!.length <= 2)) {
      section.lines.push(raw)
      continue
    }
    if (heading) {
      flush()
      flushSection()
      const level = heading[1]!.length
      while (headings.length && headings[headings.length - 1]!.level >= level)
        declined(headings.pop()!)
      headings.push({
        level,
        text: heading[2]!.trim(),
        intro: [],
        state: headingState(heading[2]!),
        line: n + 1,
        prose: false,
        items: 0,
      })
      label = ''
      if (sectionStarts.has(n)) {
        // the section is its own item: not a heading that gave none
        headings[headings.length - 1]!.items++
        section = { heading: heading[2]!.trim(), line: n + 1, lines: [] }
      }
      continue
    }
    if (raw.startsWith('|')) {
      flush()
      const block: { line: number; text: string }[] = []
      while (n < rows.length && rows[n]!.startsWith('|')) {
        block.push({ line: n + 1, text: rows[n]! })
        n++
      }
      n--
      table(block)
      continue
    }
    const item = ITEM.exec(raw)
    if (item && item[1]!.length === 0) {
      flush()
      const why: SkippedLine['why'] | null = tablesOnly
        ? 'prose in a tables file'
        : pointer
        ? 'a pointer file'
        : null
      if (why) {
        skipped.push({ line: n + 1, text: item[3]!.trim(), why })
        continue
      }
      current = { lines: [item[3]!], checked: item[2], line: n + 1 }
      continue
    }
    if (current) {
      if (raw.trim() === '') {
        // a blank line ends the item unless the next non-blank line is indented;
        // keep it and decide when that line arrives
        current.lines.push('')
        continue
      }
      if (/^[ \t]/.test(raw)) {
        current.lines.push(raw)
        continue
      }
      // a lazy continuation: an unindented line that carries on the item's
      // paragraph (no blank line before it) is still the item's (CommonMark)
      if (current.lines[current.lines.length - 1]!.trim() !== '') {
        current.idEnd ??= current.lines.length
        current.lines.push(raw)
        continue
      }
      flush()
    }
    const labelLine = LABEL_LINE.exec(raw.trim())
    if (labelLine) label = labelLine[1]!
    // prose under a heading, outside any item: the list's context
    else if (headings.length && raw.trim() && !item) {
      headings[headings.length - 1]!.intro.push(raw.trim())
      headings[headings.length - 1]!.prose = true
    }
  }
  flush()
  flushSection()
  while (headings.length) declined(headings.pop()!)
  skipped.sort((a, b) => a.line - b.line)
  // trailing blank lines an item collected while waiting for a continuation
  for (const it of items) it.body = it.body.replace(/\n+$/, '')
  return { items, skipped }
}

/** Split the top-level list items out of a notes file, with their heading paths. */
export function parseNotes(text: string, options: NotesOptions): NoteItem[] {
  return parseNotesReport(text, options).items
}

export interface NotesDraftOptions {
  by: string
  at?: string
}

/** Events for the items: a `created` each, plus a `closed` for the done ones. */
export function notesDrafts(
  items: NoteItem[],
  options: NotesDraftOptions
): EventDraft[] {
  const at = options.at ?? new Date().toISOString()
  const drafts: EventDraft[] = []
  for (const item of items) {
    drafts.push({
      id: `${item.id}:created`,
      taskId: item.id,
      at,
      by: options.by,
      kind: 'created',
      // a done item is closed, and closed is done: it lands in the done lane
      payload: {
        title: item.title,
        body: item.body,
        tags: item.closed ? [...item.tags, 'status:done'] : item.tags,
      },
    })
    if (item.closed) {
      drafts.push({
        id: `${item.id}:closed`,
        taskId: item.id,
        at,
        by: options.by,
        kind: 'closed',
        payload: { reason: 'done in the notes' },
      })
    }
  }
  return drafts
}

export interface NotesImportResult {
  project: string
  file: string
  /** items in the file (list items and Finding-table rows) */
  items: number
  /** of those, how many the file marks done */
  closed: number
  /** tasks this import created: items the store did not hold (board #180) */
  created: number
  /** events it wrote: each new task's creation, its close if done, a close a held task lacked */
  events: number
  /** what the file holds that is deliberately not a task (an UPSTREAM file's prose bullets), by line */
  skipped: SkippedLine[]
}

/** Parse a notes file and write its items into the store, idempotently. */
export async function importNotes(
  store: VirtaStore,
  text: string,
  options: NotesOptions & { at?: string }
): Promise<NotesImportResult> {
  const { items, skipped } = parseNotesReport(text, options)
  // Only what the store does not hold: an event id must always carry the
  // same body, and a re-import with a fresh `at` would not (a host's upsert
  // would replace the document and re-sequence it — tosijs-platform#25).
  const fresh = items.filter((i) => !store.get(i.id))
  const drafts = notesDrafts(fresh, { by: store.identity, at: options.at })
  for (const i of items) {
    const held = store.get(i.id)
    if (held && i.closed && held.closedAt === null) {
      const at = options.at ?? new Date().toISOString()
      // closed is done: into the done lane, then closed
      for (const tag of held.tags.filter(
        (t) => t.startsWith('status:') && t !== 'status:done'
      ))
        drafts.push({
          id: `${i.id}:untag:${tag}`,
          taskId: i.id,
          at,
          by: store.identity,
          kind: 'untagged',
          payload: { tag },
        })
      if (!held.tags.includes('status:done'))
        drafts.push({
          id: `${i.id}:done`,
          taskId: i.id,
          at,
          by: store.identity,
          kind: 'tagged',
          payload: { tag: 'status:done' },
        })
      drafts.push({
        id: `${i.id}:closed`,
        taskId: i.id,
        at,
        by: store.identity,
        kind: 'closed',
        payload: { reason: 'done in the notes' },
      })
    }
  }
  await store.import(drafts)
  return {
    project: normalizeTag(options.project),
    file: options.file ?? '',
    items: items.length,
    closed: items.filter((i) => i.closed).length,
    created: fresh.length,
    events: drafts.length,
    skipped,
  }
}

export interface NotesReconcileChange {
  taskId: string
  num: number
  /** the title it gets, when the title changes */
  title?: string
  /** the body changes */
  body: boolean
}

export interface NotesReconcileResult {
  project: string
  file: string
  /** items parsed that the store holds */
  held: number
  /** what changes (or would, on a dry run) */
  changes: NotesReconcileChange[]
  /** tasks whose title or body a person or agent changed since the import: left alone */
  kept: { num: number; title: string; why: string }[]
  /** events written (0 on a dry run) */
  written: number
}

/**
 * Bring tasks imported by an earlier importer up to what the importer makes
 * now (board #164) — never a wipe. For each item the store holds, the title
 * and the body are rewritten only where nobody has touched them since the
 * import (no `retitled` / `edited` event); anything changed by hand is kept
 * and listed. The writes are events like any other — `retitled`, `edited`
 * — with ids derived from the item and the new text, so running it again
 * adds nothing. `apply: false` (the default) only reports.
 */
export async function reconcileNotes(
  store: VirtaStore,
  text: string,
  options: NotesOptions & { at?: string; apply?: boolean }
): Promise<NotesReconcileResult> {
  const items = parseNotes(text, options)
  const at = options.at ?? new Date().toISOString()
  const drafts: EventDraft[] = []
  const changes: NotesReconcileChange[] = []
  const kept: NotesReconcileResult['kept'] = []
  let held = 0
  const stamp = (s: string) => hashText(s).toString(16).padStart(8, '0')
  for (const item of items) {
    const task = store.get(item.id)
    if (!task) continue
    held++
    const kinds = new Set(store.events(item.id).map((e) => e.kind))
    const change: NotesReconcileChange = {
      taskId: task.id,
      num: task.num,
      body: false,
    }
    if (task.title !== item.title) {
      if (kinds.has('retitled'))
        kept.push({ num: task.num, title: task.title, why: 'retitled since' })
      else {
        change.title = item.title
        drafts.push({
          id: `${item.id}:reconcile:title:${stamp(item.title)}`,
          taskId: item.id,
          at,
          by: store.identity,
          kind: 'retitled',
          payload: { title: item.title },
        })
      }
    }
    if (task.body !== item.body) {
      if (kinds.has('edited'))
        kept.push({ num: task.num, title: task.title, why: 'edited since' })
      else {
        change.body = true
        drafts.push({
          id: `${item.id}:reconcile:body:${stamp(item.body)}`,
          taskId: item.id,
          at,
          by: store.identity,
          kind: 'edited',
          payload: { body: item.body },
        })
      }
    }
    if (change.title !== undefined || change.body) changes.push(change)
  }
  if (options.apply) await store.import(drafts)
  return {
    project: normalizeTag(options.project),
    file: options.file ?? '',
    held,
    changes,
    kept,
    written: options.apply ? drafts.length : 0,
  }
}
